import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as nyc from 'ui-library/NYC';
import useRecentWorklistsQuery from 'worklists/useRecentWorklistsQuery';
import WorklistCreateButton from 'worklists/WorklistCreateButton';
import {openWorklistListing} from 'worklists/WorklistUIActions';
import {formatWorklistDate} from 'worklists/WorklistUtil';

import {WorklistSummaryGraphQL} from '../../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';

/**
 * This component shows recently modified worklists in the home sidebar, ordered
 * by when they were last modified.
 *
 * This component assumes that the worklists array passed in is already sorted
 * by modifiedAt descending, because the GQL field should return them in that
 * order.
 */
export default function RecentWorklistsHomeListing(): JSX.Element | null {
  const {queryResultType, recentWorklists} = useRecentWorklistsQuery();
  if (queryResultType === QueryResultType.ERROR) {
    return null;
  }

  const worklistRows = [];
  if (queryResultType === QueryResultType.PENDING) {
    worklistRows.push(
      <nyc.Row key="loading-worklists">
        <div className="homeSidepanel-today-item">Loading worklists...</div>
      </nyc.Row>
    );
  } else if (recentWorklists!.length === 0) {
    worklistRows.push(
      <nyc.Row key="no-worklists">
        <div
          {...getTestProps({component: 'RecentWorklistRow', element: 'noRecentWorklist'})}
          className="homeSidepanel-today-item"
        >
          You have no recent worklists.
        </div>
      </nyc.Row>
    );
  } else {
    for (const [index, wl] of recentWorklists!.entries()) {
      worklistRows.push(<RecentWorklistRow key={index} worklist={wl} />);
    }
    worklistRows.push(
      <a
        key={worklistRows.length}
        href=""
        className="whiteLink"
        onClick={(e) => {
          e.preventDefault();
          SidepanelActions.openSection(Section.WORKLISTS);
        }}
        {...getTestProps({component: 'RecentWorklistRow', element: 'seeMore'})}
      >
        See more...
      </a>
    );
  }

  return <RecentWorklistsContainer worklistRows={worklistRows} />;
}

interface RecentWorklistsContainerProps {
  worklistRows: Array<JSX.Element>;
}

function RecentWorklistsContainer(props: RecentWorklistsContainerProps): JSX.Element {
  return (
    <nyc.Container size="sidepanel" className="homeSidepanel-today-itemSummary">
      <nyc.Row className="homeSidepanel-today-sectionHeader u-pointer">
        <div
          className="homeSidepanel-today-sectionHeader-content"
          onClick={() => {
            SidepanelActions.openSection(Section.WORKLISTS);
          }}
          {...getTestProps({component: 'RecentWorklistsTitleContainer', element: 'worklistTitle'})}
        >
          Recent worklists
        </div>
        <WorklistCreateButton buttonStyle={ButtonStyle.TERTIARY_WHITE_TRANSPARENT} />
      </nyc.Row>
      <div {...getTestProps({component: 'RecentWorklistsContainer', element: 'worklistRows'})}>
        {props.worklistRows}
      </div>
    </nyc.Container>
  );
}

export interface RecentWorklistRowProps {
  worklist: WorklistSummaryGraphQL;
}

export function RecentWorklistRow(props: RecentWorklistRowProps): JSX.Element {
  const dateString = formatWorklistDate(props.worklist.modifiedAt, true);
  return (
    <nyc.Row className="homeSidepanel-today-item">
      <div className="homeSidepanel-today-item-icon">
        <BenchlingIcon icon={ModelTypeConstants.WORKLIST} />
      </div>
      <div
        className="homeSidepanel-today-item-title whiteLink"
        onClick={(e) => {
          openWorklistListing(props.worklist);
        }}
        {...getTestProps({component: 'RecentWorklistRow', element: 'worklistTitle'})}
      >
        {props.worklist.name}
      </div>
      <div className="homeSidepanel-today-item-subtitle u-small-spacing-left">{dateString}</div>
    </nyc.Row>
  );
}
